import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Size} from '@mintdata/mint-kit/api/models/Size';
import bind from 'bind-decorator';
import {reaction} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {ReactElement} from 'react';

const _ResizeSensor = require('css-element-queries/src/ResizeSensor');

export interface IResizeSensorProps
{
    index?:number;
    onResize?:(size:Size) => void;
}

export interface IResizeSensorState
{
    size:Size;
}

/**
 * Re-renders the target every time it size changes
 * @prop size {Size} new element size
 * */
@observer
export class ResizeSensor extends React.Component<IResizeSensorProps, IResizeSensorState>
{
    private _elementRef:HTMLDivElement;
    private _resizeSensor:any;
    private _reactionDisposer:any;

    @bind
    private _resetResizeSensor()
    {

        // Temp hack to fix ResizeSensor after move an element in the DOM
        // until ResizeSensor.reset() is not implemented
        // see https://github.com/marcj/css-element-queries/issues/171
        if( !this._elementRef ) return;

        const expand = this._elementRef.querySelector('.resize-sensor-expand');
        const shrink = this._elementRef.querySelector('.resize-sensor-shrink');

        if( !expand || !shrink ) return;

        const expandChild = expand.childNodes[0] as HTMLDivElement;

        if( !expandChild ) return;

        expandChild.style.width = '100000px';
        expandChild.style.height = '100000px';

        expand.scrollLeft = 100000;
        expand.scrollTop = 100000;

        shrink.scrollLeft = 100000;
        shrink.scrollTop = 100000;
    }

    public constructor(props:any)
    {
        super(props);
        this.state = {size: Size.zero()};
    }

    private _getContentSize():Size
    {
        return new Size(
            this._elementRef.clientWidth,
            this._elementRef.clientHeight,
        );
    }

    @bind
    private _onResize():void
    {
        const {onResize} = this.props;

        const size:Size = this._getContentSize();

        this.setState({size});

        if( onResize )
            onResize(size);
    }

    public componentWillUnmount()
    {
        this._resizeSensor.detach();
        this._reactionDisposer();
    }

    public componentWillReceiveProps({index})
    {
        // Suppose element was moved if the index has changed,
        // so ResizeSensor should be resetted
        if( index != this.props.index )
        {
            this._resetResizeSensor();
        }
    }

    public componentDidMount()
    {
        this._onResize();

        this._reactionDisposer = reaction(() =>
            {
                const {layoutUpdate} = rootState.ui.visualDesigner.surfaceEditor;
                const {counter} = layoutUpdate;

                return counter;
            },
            () =>
            {
                setTimeout(() =>
                {
                    this._resetResizeSensor();
                }, 0);
            });

        this._resizeSensor = new _ResizeSensor(this._elementRef, () =>
        {
            requestAnimationFrame(this._onResize);
        });
    }

    public render():JSX.Element
    {
        const {children} = this.props;
        const {size} = this.state;

        const childrenWithProps = React.Children.map(children, (child:ReactElement<any>) =>
            React.cloneElement(child, {...this.props, size}, child.props.children)
        );

        return (
            <div
                style={{height: '100%', width: '100%', overflow: 'visible'}}
                ref={(elementRef =>
                {
                    if( !elementRef ) return;
                    this._elementRef = elementRef;
                })}
            >
                {childrenWithProps}
            </div>
        );
    }
}
